import { animate, keyframes, style, transition, trigger } from '@angular/animations';
import { DOCUMENT, Location, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { LOCAL_STORAGE } from '@ng-web-apis/common';
import { I18NextModule } from 'angular-i18next';
import { interval, Subscription } from 'rxjs';
import { GoogleAnalyticsService } from 'src/app/common/analytics/google-analytics.service';
import { BasePlusComponent } from 'src/app/_shared/components/base/base-plus.component';
import { SignInService } from 'src/app/_shared/dialogs/sign-in/sign-in.service';
import { IconComponent } from 'src/app/common/icon/icon.component';
import { METADATA_SERVICE, MetadataService } from 'src/app/common/metadata/metadata.service';
import { OpenDialogService } from 'src/app/common/open-dialog/open-dialog.service';
import { RouteTranslatePipe } from 'src/app/_shared/pipes/route-translate.pipe';
import { UserService } from 'src/app/common/user/user.service';
import { AttachedDocument, BillingInfoInput, GalleryContentLicenseEnum } from 'src/types/graphql';
import { FooterComponent } from '../../_shared/components/footer/footer.component';
import { HeaderComponent } from '../../_shared/components/header/header.component';

@Component({
  templateUrl: './become-pro.component.html',
  styleUrls: ['./become-pro.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideInOut', [
      transition('* => *', [
        style({ opacity: 0, transform: 'translateY(-100%)' }),
        animate(
          '4s ease-in-out',
          keyframes([
            style({ opacity: 0, transform: 'translateY(-100%)', offset: 0 }),
            style({ opacity: 1, transform: 'translateY(0)', offset: 0.2 }),
            style({ opacity: 1, transform: 'translateY(0)', offset: 0.9 }),
            style({ opacity: 0, transform: 'translateY(10%)', offset: 1 }),
          ]),
        ),
      ]),
    ]),
  ],
  imports: [
    NgClass,
    MatButtonModule,
    I18NextModule,
    RouterLink,
    RouteTranslatePipe,
    MatExpansionModule,
    IconComponent,
    HeaderComponent,
    FooterComponent,
    NgTemplateOutlet,
  ],
})
export class BecomeProComponent extends BasePlusComponent implements OnInit, OnDestroy {
  heroIndex = 0;
  heroItems = ['your-organization', 'shop', 'stripe', 'widget', 'support'];
  colNumber = 3;
  gallerySubscription: Subscription;
  galleryIndex = 0;
  galleryItems: { src: string; name: string; businessName: string; businessKind: string; quotation: string }[] = [
    {
      src: 'https://media.sharry.land/app/images/become-pro/guida-turistica.webp',
      name: 'become-pro.all-in-one.section-4.name',
      businessName: 'become-pro.all-in-one.section-4.businessName',
      businessKind: 'become-pro.all-in-one.section-4.businessKind',
      quotation: 'become-pro.all-in-one.section-4.quotation',
    },
    {
      src: 'https://media.sharry.land/app/images/become-pro/struttura-ricettiva.webp',
      name: 'become-pro.all-in-one.section-1.name',
      businessName: 'become-pro.all-in-one.section-1.businessName',
      businessKind: 'become-pro.all-in-one.section-1.businessKind',
      quotation: 'become-pro.all-in-one.section-1.quotation',
    },
    {
      src: 'https://media.sharry.land/app/images/become-pro/enogastronomia.webp',
      name: 'become-pro.all-in-one.section-5.name',
      businessName: 'become-pro.all-in-one.section-5.businessName',
      businessKind: 'become-pro.all-in-one.section-5.businessKind',
      quotation: 'become-pro.all-in-one.section-5.quotation',
    },
    {
      src: 'https://media.sharry.land/app/images/become-pro/artigiano.webp',
      name: 'become-pro.all-in-one.section-3.name',
      businessName: 'become-pro.all-in-one.section-3.businessName',
      businessKind: 'become-pro.all-in-one.section-3.businessKind',
      quotation: 'become-pro.all-in-one.section-3.quotation',
    },
    {
      src: 'https://media.sharry.land/app/images/become-pro/servizi-noleggio.webp',
      name: 'become-pro.all-in-one.section-2.name',
      businessName: 'become-pro.all-in-one.section-2.businessName',
      businessKind: 'become-pro.all-in-one.section-2.businessKind',
      quotation: 'become-pro.all-in-one.section-2.quotation',
    },
    {
      src: 'https://media.sharry.land/app/images/become-pro/azienda-agricola.webp',
      name: 'become-pro.all-in-one.section-6.name',
      businessName: 'become-pro.all-in-one.section-6.businessName',
      businessKind: 'become-pro.all-in-one.section-6.businessKind',
      quotation: 'become-pro.all-in-one.section-6.quotation',
    },
    {
      src: 'https://media.sharry.land/app/images/become-pro/beni-culturali.webp',
      name: 'become-pro.all-in-one.section-7.name',
      businessName: 'become-pro.all-in-one.section-7.businessName',
      businessKind: 'become-pro.all-in-one.section-7.businessKind',
      quotation: 'become-pro.all-in-one.section-7.quotation',
    },
    {
      src: 'https://media.sharry.land/app/images/become-pro/associazioni.webp',
      name: 'become-pro.all-in-one.section-8.name',
      businessName: 'become-pro.all-in-one.section-8.businessName',
      businessKind: 'become-pro.all-in-one.section-8.businessKind',
      quotation: 'become-pro.all-in-one.section-8.quotation',
    },
  ];
  accordionPanels: { id: string; title: string; desc: string }[] = [
    { id: 'panel-1', title: 'become-pro.faq.1.title', desc: 'become-pro.faq.1.desc' },
    { id: 'panel-2', title: 'become-pro.faq.2.title', desc: 'become-pro.faq.2.desc' },
    { id: 'panel-3', title: 'become-pro.faq.3.title', desc: 'become-pro.faq.3.desc' },
    { id: 'panel-4', title: 'become-pro.faq.4.title', desc: 'become-pro.faq.4.desc' },
  ];
  quotations: { review: string; reviewer: string; reviewerDesc: string; src: string }[] = [
    {
      review: 'become-pro.about-us.review-1',
      reviewer: 'become-pro.about-us.reviewer-4',
      reviewerDesc: 'become-pro.about-us.reviewer-desc-1',
      src: 'https://media.sharry.land/app/images/become-pro/martina-stortini.png',
    },
    {
      review: 'become-pro.about-us.review-2',
      reviewer: 'become-pro.about-us.reviewer-5',
      reviewerDesc: 'become-pro.about-us.reviewer-desc-2',
      src: 'https://media.sharry.land/app/images/become-pro/distretto-bio.png',
    },
    {
      review: 'become-pro.about-us.review-3',
      reviewer: 'become-pro.about-us.reviewer-6',
      reviewerDesc: 'become-pro.about-us.reviewer-desc-3',
      src: 'https://media.sharry.land/app/images/become-pro/como-gal.png',
    },
  ];
  services: service[] = [
    {
      iconType: 'phosphor',
      icon: 'handbag',
      text: 'appointments',
    },
    {
      iconType: 'phosphor',
      icon: 'gear',
      text: 'backoffice',
    },
    {
      iconType: 'phosphor',
      icon: 'credit-card',
      text: 'payment',
    },
    {
      iconType: 'phosphor',
      icon: 'heart',
      text: 'social',
    },
    {
      iconType: 'phosphor',
      icon: 'mouse',
      text: 'widget',
    },
    {
      iconType: 'phosphor',
      icon: 'headset',
      text: 'support',
    },
  ];

  constructor(
    private renderer: Renderer2,
    public userService: UserService,
    private router: Router,
    private route: ActivatedRoute,
    private googleAnalyticsService: GoogleAnalyticsService,
    private cd: ChangeDetectorRef,
    private signInService: SignInService,
    private location: Location,
    private openDialogService: OpenDialogService,
    @Inject(METADATA_SERVICE) private metadataService: MetadataService,
    @Inject(LOCAL_STORAGE) private localStorage: Storage,
    @Inject(DOCUMENT) private document: Document,
  ) {
    super();
  }

  @HostListener('window:resize') onResize() {
    this.checkMobile();
  }

  ngOnInit(): void {
    this.renderer.addClass(this.document.querySelector('html'), 'shrink');
    this.metadataService.setMetadataPage('become-pro', this.location.path());

    this.checkMobile();
    if (this.isPlatformBrowser()) {
      this.subscriptions.push(
        (this.gallerySubscription = interval(5000).subscribe(() => {
          if (this.galleryIndex === this.galleryItems.length - 1) {
            this.galleryIndex = 0;
            this.cd.detectChanges();
          } else {
            ++this.galleryIndex;
            this.cd.detectChanges();
          }
        })),
      );
    }
    this.subscriptions.push(
      this.signInService.signIn.subscribe(() => {
        this.cd.detectChanges();
      }),
    );
  }

  checkMobile(): void {
    this.colNumber = this.isMobile() ? 2 : this.isMobileNotIpad() ? 1 : 3;
  }

  openSupportDialog(): void {
    import('../../_shared/dialogs/support-dialog/support-dialog.component').then(({ SupportDialogComponent }) =>
      this.openDialogService.openDialog(SupportDialogComponent, {
        ...{
          autoFocus: false,
          restoreFocus: false,
          width: '90vw',
          maxWidth: '960px',
          minHeight: '200px',
          maxHeight: '84vh',
        },
      }),
    );
  }

  openModal(buttonType: string, fromCards: boolean) {
    if (!this.userService.user) {
      this.googleAnalyticsService.logCustomEvent('sign_up', 'Click: become ' + buttonType + (fromCards ? ' card' : ''));
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          signup: 'true',
        },
        queryParamsHandling: 'merge',
        skipLocationChange: false,
      });
    }
  }

  setSmallOrganization(): void {
    this.localStorage.setItem('signup-isSmallOrganisation', 'true');
  }

  openStripeDialog(): void {
    import('../../_shared/dialogs/stripe-dialog/stripe-dialog.component').then(({ StripeDialogComponent }) =>
      this.openDialogService.openDialog(StripeDialogComponent, {
        ...{
          autoFocus: false,
          restoreFocus: false,
          minWidth: '70vw',
          maxHeight: '84vh',
        },
      }),
    );
  }

  resetGalleryTimer(): void {
    this.gallerySubscription.unsubscribe();
    this.gallerySubscription = interval(5000).subscribe(() => {
      if (this.galleryIndex === this.galleryItems.length - 1) {
        this.galleryIndex = 0;
        this.cd.detectChanges();
      } else {
        ++this.galleryIndex;
        this.cd.detectChanges();
      }
    });
  }

  animationDone(): void {
    this.heroIndex = (this.heroIndex + 1) % this.heroItems.length;
  }

  ngOnDestroy() {
    this.metadataService.clean();
    this.renderer.removeClass(this.document.querySelector('html'), 'shrink');
  }
}

export interface CardContent {
  icon: string;
  title: string;
  subtitle: string;
  currency: string;
  currencyValue: number;
  currencyDesc: string;
  button: string;
  features: string[];
  featuresLabel?: string;
  tax?: string;
  proModalData?: ModalContent;
  ccData?: any;
  billingData?: BillingInfoInput;
}

export interface ModalContent {
  title: string;
  subtitle: string;
  button: string;
  profile: ModalContentInner;
  wallpaper: ModalContentInner;
  whoYouAre: ModalContentInner;
  selfDeclaration: ModalContentInner;
  profession: ModalContentInner;
  business: ModalContentInner;
  location: ModalContentInner;
  languages: ModalContentInner;
  categories: ModalContentInner;
  gold: boolean;
  adHoc: boolean;
  sessionPayload: ProSessionData;
}

export interface ModalContentInner {
  title: string;
  subtitle: string;
  checkboxLabel?: string;
}

export class FileUploaded {
  file: File;
  value: {
    url: string;
    author?: string;
    copyrightLink?: string;
    description?: string;
    licenseType?: GalleryContentLicenseEnum;
    __typename?: string;
  };

  constructor(
    _file?: File,
    _value?: {
      url: string;
      author?: string;
      copyrightLink?: string;
      description?: string;
      licenseType?: GalleryContentLicenseEnum;
      __typename?: string;
    },
  ) {
    this.file = _file;
    this.value = _value;
  }
}

export class ProSessionData {
  image: FileUploaded;
  whoIAm: string;
  whatICanDo: string;
  latitude: string;
  workAddress: string;
  longitude: string;
  languagesIds: string[];
  categoriesIds: string[];
  qualificationId: string;
  region: string;
  province: string;
  shortProvince: string;
  attachedDocuments: AttachedDocument[];
  contactPhone: string;
  contactEmail: string;
  websiteUrl: string;
  contactWhatsapp: string;
  facebookUrl: string;
  instagramUrl: string;
}

export interface service {
  iconType: 'phosphor' | 'custom';
  icon: string;
  text: string;
}
